import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Tabs, 
  Tab, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  LinearProgress,
  Avatar 
} from '@material-ui/core';
import { 
  Publish, 
  CheckCircle, 
  Timeline, 
  TrendingUp,
  ExpandMore,
  Error,
  CloudDownload,
  BugReport 
} from '@material-ui/icons';
import { Link } from "@backstage/core-components";
import { 
  ProjectReleaseData, 
  Release,
  ReleaseDefinition,
  AutomergeFailure 
} from '../types';
import { 
  getReleaseStatusIcon, 
  getReleaseStatusChip, 
  getEnvironmentStatusIcon,
  getAutomergeStatusColor,
  getReleaseStatusColor,
  formatDate, 
  formatDateShort, 
  formatDuration,
  formatHours 
} from '../utils';
import { TabPanel } from '../shared';

// Release overview cards component
interface ReleaseOverviewCardsProps {
  releaseData: ProjectReleaseData;
}

export const ReleaseOverviewCards: React.FC<ReleaseOverviewCardsProps> = ({ releaseData }) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Publish style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3' }}>
            {releaseData.totalReleases}
          </Typography>
          <Typography variant="body2" color="textSecondary">Total Releases</Typography>
          <Typography variant="caption" style={{ color: '#4caf50' }}>
            {releaseData.activeReleases} active
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CheckCircle style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {releaseData.overallSuccessRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">Success Rate</Typography>
          <Typography variant="caption" color="textSecondary">
            {releaseData.successfulReleases} successful
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <TrendingUp style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#ff9800' }}>
            {releaseData.releaseMetrics.deploymentsPerWeek.toFixed(1)}
          </Typography>
          <Typography variant="body2" color="textSecondary">Deployments/Week</Typography>
          <Typography variant="caption" color="textSecondary">
            Every {releaseData.releaseMetrics.deploymentsPerWeek > 0 ? Math.round(7 / releaseData.releaseMetrics.deploymentsPerWeek) : 'N/A'} days
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Timeline style={{ fontSize: 40, color: '#9c27b0', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#9c27b0' }}>
            {formatHours(releaseData.releaseMetrics.averageLeadTime)}
          </Typography>
          <Typography variant="body2" color="textSecondary">Avg Lead Time</Typography>
          <Typography variant="caption" color="textSecondary">
            Commit to production
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Release list component
interface ReleaseListProps {
  releases: Release[];
  onReleaseSelect: (release: Release) => void;
}

export const ReleaseList: React.FC<ReleaseListProps> = ({ releases, onReleaseSelect }) => (
  <Grid container spacing={3}>
    {releases.map((release: Release) => (
      <Grid item xs={12} md={6} lg={4} key={release.id}>
        <Card 
          elevation={1} 
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onReleaseSelect(release)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ marginBottom: '4px' }}>
                  {release.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  {release.description}
                </Typography>
                <Typography variant="caption" color="primary" style={{ marginBottom: '8px', display: 'block' }}>
                  <Link 
                    to={release.webAccessUri} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View in Azure DevOps
                  </Link>
                </Typography>
              </div>
              {getReleaseStatusIcon(release.status)}
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              {getReleaseStatusChip(release.status)}
              <Chip 
                size="small" 
                label={release.reason} 
                variant="outlined" 
                style={{ marginLeft: '8px' }}
              />
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Created By</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.createdBy.displayName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Environments</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.environments.length}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Created</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {formatDateShort(release.createdOn)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Keep Forever</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.keepForever ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>

            <div style={{ marginTop: '12px' }}>
              <Typography variant="caption" color="textSecondary">
                Definition: {release.releaseDefinition.name}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Release definitions component
interface ReleaseDefinitionsProps {
  releaseDefinitions: ReleaseDefinition[];
}

export const ReleaseDefinitions: React.FC<ReleaseDefinitionsProps> = ({ releaseDefinitions }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Release Definitions ({releaseDefinitions.length})
    </Typography>
    {releaseDefinitions.map((definition) => (
      <Accordion key={definition.id}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6">{definition.name}</Typography>
            <div style={{ display: 'flex', gap: '8px', marginRight: '16px' }}>
              {definition.lastRelease && (
                <Chip 
                  size="small" 
                  label={`Last: ${definition.lastRelease.name}`}
                  style={{ backgroundColor: '#2196f3', color: 'white' }}
                />
              )}
              <Chip 
                size="small" 
                label={`${definition.environments.length} environments`} 
                variant="outlined"
              />
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ width: '100%' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Description:</strong> {definition.description || 'No description provided'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Path:</strong> {definition.path}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Release Name Format:</strong> {definition.releaseNameFormat}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Source:</strong> {definition.source}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Created By:</strong> {definition.createdBy.displayName}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Created On:</strong> {formatDate(definition.createdOn)}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Modified By:</strong> {definition.modifiedBy.displayName}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  <strong>Modified On:</strong> {formatDate(definition.modifiedOn)}
                </Typography>
              </Grid>
            </Grid>
            
            <Typography variant="subtitle2" style={{ marginTop: '16px', marginBottom: '8px' }}>
              Environments:
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {definition.environments.map((env: ReleaseDefinition['environments'][number]) => (
                <Chip 
                  key={env.id}
                  label={`${env.rank}. ${env.name}`}
                  variant="outlined"
                  size="small"
                />
              ))}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    ))}
  </div>
);

// Environment statistics component
interface EnvironmentStatsProps {
  environmentStats: ProjectReleaseData['environmentStats'];
}

export const EnvironmentStats: React.FC<EnvironmentStatsProps> = ({ environmentStats }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Environment Deployment Statistics
    </Typography>
    <Grid container spacing={3}>
      {environmentStats.map((env: ProjectReleaseData['environmentStats'][number], index: number) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" style={{ marginBottom: '12px' }}>
                {env.name}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Total Deployments</Typography>
                  <Typography variant="h6" style={{ color: '#2196f3' }}>
                    {env.totalDeployments}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Success Rate</Typography>
                  <Typography variant="h6" style={{ color: '#4caf50' }}>
                    {env.totalDeployments > 0 ? ((env.successfulDeployments / env.totalDeployments) * 100).toFixed(1) : 0}%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Successful</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {env.successfulDeployments}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Failed</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                    {env.failedDeployments}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Avg Deployment Time</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {formatDuration(env.averageDeploymentTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Last Deployment</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {formatDate(env.lastDeployment)}
                  </Typography>
                </Grid>
              </Grid>
              <LinearProgress 
                variant="determinate" 
                value={env.totalDeployments > 0 ? (env.successfulDeployments / env.totalDeployments) * 100 : 0}
                style={{ 
                  marginTop: '12px',
                  backgroundColor: '#f0f0f0',
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </div>
);

// Automerge failures component
interface AutomergeFailuresProps {
  automergeFailures: AutomergeFailure[];
}

export const AutomergeFailures: React.FC<AutomergeFailuresProps> = ({ automergeFailures }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Automerge Failures ({automergeFailures.length})
    </Typography>
    {automergeFailures.length > 0 ? (
      <Grid container spacing={2}>
        {automergeFailures.map((failure, index) => (
          <Grid item xs={12} key={index}>
            <Card elevation={1} style={{ marginBottom: '12px' }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BugReport style={{ 
                        color: getAutomergeStatusColor(failure.status), 
                        fontSize: 20 
                      }} />
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          PR #{failure.pullRequestId}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {failure.repositoryName}
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {failure.sourceBranch} → {failure.targetBranch}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      by {failure.author}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Chip 
                      size="small" 
                      label={failure.status}
                      style={{ 
                        backgroundColor: getAutomergeStatusColor(failure.status),
                        color: 'white'
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px' }}>
                      {failure.retryAttempts} retries
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(failure.failureDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ marginBottom: '8px' }}>
                      <strong>Reason:</strong> {failure.failureReason}
                    </Typography>
                    {failure.conflictFiles.length > 0 && (
                      <div>
                        <Typography variant="caption" color="textSecondary">
                          Conflict Files:
                        </Typography>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {failure.conflictFiles.map((file: string, fileIndex: number) => (
                            <Chip 
                              key={fileIndex}
                              label={file}
                              size="small"
                              variant="outlined"
                              style={{ fontSize: '0.7em' }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    ) : (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircle style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Automerge Failures
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All automated merges are functioning properly
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Release metrics and KPIs component
interface ReleaseMetricsProps {
  releaseData: ProjectReleaseData;
}

export const ReleaseMetrics: React.FC<ReleaseMetricsProps> = ({ releaseData }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Release Metrics & Key Performance Indicators
    </Typography>
    
    <Grid container spacing={3} style={{ marginBottom: '24px' }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <CloudDownload style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
            <Typography variant="h5" style={{ color: '#2196f3' }}>
              {releaseData.releaseMetrics.deploymentsPerWeek.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Deployments per Week
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Deployment Frequency
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Timeline style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
            <Typography variant="h5" style={{ color: '#4caf50' }}>
              {formatHours(releaseData.releaseMetrics.averageLeadTime)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Average Lead Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Commit to Production
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <BugReport style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
            <Typography variant="h5" style={{ color: '#ff9800' }}>
              {formatHours(releaseData.releaseMetrics.meanTimeToRecovery)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Mean Time to Recovery
            </Typography>
            <Typography variant="caption" color="textSecondary">
              MTTR
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Error style={{ fontSize: 40, color: '#f44336', marginBottom: '8px' }} />
            <Typography variant="h5" style={{ color: '#f44336' }}>
              {releaseData.releaseMetrics.changeFailureRate.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Change Failure Rate
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Failed Deployments
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card elevation={1}>
          <CardContent>
            <Typography variant="h6" style={{ marginBottom: '16px' }}>
              Recent Successful Releases
            </Typography>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {releaseData.recentSuccessfulReleases.slice(0, 10).map((release: Release, index: number) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < 9 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle style={{ color: '#4caf50', fontSize: 16 }} />
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {release.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {release.releaseDefinition.name}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {release.createdBy.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDateShort(release.createdOn)}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card elevation={1}>
          <CardContent>
            <Typography variant="h6" style={{ marginBottom: '16px' }}>
              Failed Releases
            </Typography>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {releaseData.failedReleases.slice(0, 10).map((release: Release, index: number) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < 9 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Error style={{ color: '#f44336', fontSize: 16 }} />
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {release.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {release.releaseDefinition.name}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {release.createdBy.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDateShort(release.createdOn)}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </div>
);

// Release tabs component
interface ReleaseTabsProps {
  releaseData: ProjectReleaseData;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  onReleaseSelect: (release: Release) => void;
}

export const ReleaseTabs: React.FC<ReleaseTabsProps> = ({ 
  releaseData, 
  tabValue, 
  onTabChange, 
  onReleaseSelect 
}) => (
  <Card>
    <Tabs
      value={tabValue}
      onChange={onTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="All Releases" />
      <Tab label="Release Definitions" />
      <Tab label="Environment Stats" />
      <Tab label="Automerge Failures" />
      <Tab label="Metrics & KPIs" />
    </Tabs>

    <TabPanel value={tabValue} index={0}>
      <ReleaseList releases={releaseData.releases} onReleaseSelect={onReleaseSelect} />
    </TabPanel>

    <TabPanel value={tabValue} index={1}>
      <ReleaseDefinitions releaseDefinitions={releaseData.releaseDefinitions} />
    </TabPanel>

    <TabPanel value={tabValue} index={2}>
      <EnvironmentStats environmentStats={releaseData.environmentStats} />
    </TabPanel>

    <TabPanel value={tabValue} index={3}>
      <AutomergeFailures automergeFailures={releaseData.automergeFailures} />
    </TabPanel>

    <TabPanel value={tabValue} index={4}>
      <ReleaseMetrics releaseData={releaseData} />
    </TabPanel>
  </Card>
);

// Release detail view component
interface ReleaseDetailProps {
  release: Release;
}

export const ReleaseDetail: React.FC<ReleaseDetailProps> = ({ release }) => (
  <div>
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
              {release.name}
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
              {release.description || 'No description provided'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
              <strong>Definition:</strong> {release.releaseDefinition.name} • 
              <strong>Path:</strong> {release.releaseDefinition.path}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <Link 
                to={release.webAccessUri} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Release in Azure DevOps
              </Link>
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getReleaseStatusIcon(release.status)}
            {getReleaseStatusChip(release.status)}
            <Chip 
              size="small" 
              label={release.reason} 
              variant="outlined"
            />
            {release.keepForever && (
              <Chip 
                size="small" 
                label="Keep Forever" 
                style={{ backgroundColor: '#9c27b0', color: 'white' }}
              />
            )}
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{release.environments.length}</Typography>
              <Typography variant="body2" color="textSecondary">Environments</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{release.createdBy.displayName}</Typography>
              <Typography variant="body2" color="textSecondary">Created By</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{formatDateShort(release.createdOn)}</Typography>
              <Typography variant="body2" color="textSecondary">Created Date</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{release.tags.length}</Typography>
              <Typography variant="body2" color="textSecondary">Tags</Typography>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Environment Deployments
            </Typography>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {release.environments.map((env: Release['environments'][number], index: number) => (
                <Card key={index} variant="outlined" style={{ marginBottom: '12px' }}>
                  <CardContent style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <Typography variant="h6" style={{ marginBottom: '4px' }}>
                          {env.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Trigger: {env.triggerReason || 'Manual'}
                        </Typography>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getEnvironmentStatusIcon(env.status)}
                        <Chip 
                          size="small" 
                          label={env.status || 'Unknown'} 
                          style={{ 
                            backgroundColor: getReleaseStatusColor(env.status || 'unknown'), 
                            color: 'white' 
                          }}
                        />
                      </div>
                    </div>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Deploy Started:</Typography>
                        <Typography variant="body2">
                          {env.deployStartedOn ? formatDate(env.deployStartedOn) : 'Not started'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Deploy Completed:</Typography>
                        <Typography variant="body2">
                          {env.deployCompletedOn ? formatDate(env.deployCompletedOn) : 'Not completed'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {env.deployStartedOn && env.deployCompletedOn && (
                      <div style={{ marginTop: '12px' }}>
                        <Typography variant="body2" color="textSecondary">Duration:</Typography>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {formatDuration(
                            (new Date(env.deployCompletedOn).getTime() - new Date(env.deployStartedOn).getTime()) / (1000 * 60)
                          )}
                        </Typography>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {release.environments.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ padding: '20px', textAlign: 'center' }}>
                  No environments configured for this release
                </Typography>
              )}
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card style={{ marginBottom: '20px' }}>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Release Information
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Release Definition:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.releaseDefinition.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <Link 
                    to={release.releaseDefinition.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    View Definition
                  </Link>
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Created By:</Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Avatar 
                    src={release.createdBy.imageUrl} 
                    style={{ width: 24, height: 24 }}
                  >
                    {release.createdBy.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {release.createdBy.displayName}
                  </Typography>
                </div>
                <Typography variant="caption" color="textSecondary">
                  {release.createdBy.uniqueName}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Created Date:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {formatDate(release.createdOn)}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Modified By:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.modifiedBy.displayName}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                  {formatDate(release.modifiedOn)}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Trigger Reason:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {release.reason}
                </Typography>
              </div>
              
              {release.comment && (
                <div>
                  <Typography variant="body2" color="textSecondary">Comment:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {release.comment}
                  </Typography>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {release.tags.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
                Tags
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {release.tags.map((tag: string, index: number) => (
                  <Chip 
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  </div>
);